<template>
  <NSpace vertical>
    <NForm ref="configFormRef" :model="config" :rules="rules">
      <div
        v-for="(section, sectionIdx) in template?.sections"
        :key="sectionIdx"
        class="template-section"
        :class="{ 'template-section--has-next': template?.sections && sectionIdx !== template.sections.length - 1 }"
      >
        <h3 v-if="section.label" class="mt-2 mb-4">{{ section.label }}</h3>

        <NFormItem
          v-for="(field, fieldIdx) in section.fields"
          :key="`${sectionIdx}-${fieldIdx}`"
          :class="['config-field', `config-field--${toCssClassKey(field.name)}`]"
          :label="field.label"
          :path="field.name"
          :required="!!field.is_required && !ignoreRequired"
          :show-label="FieldTypes.CHECKBOX !== field.type || showKeepAsIsPlaceholder"
        >
          <template v-if="field.type === FieldTypes.NUMBER">
            <CInputPlaceholder
              v-if="showKeepAsIsPlaceholder && !isEditingFieldOrNonEmpty(field.name)"
              @click="enableEditing(field.name, field.type)"
            >
              <template #suffix>
                <div class="flex items-center gap-1">
                  <CIcon fa-type="fal" icon="minus" size="16px" />
                  <CIcon fa-type="fal" icon="plus" size="16px" />
                </div>
              </template>
            </CInputPlaceholder>
            <NInputNumber
              v-else
              :ref="(el) => (inputRefs[field.name] = el as InstanceType<typeof NInputNumber> | null)"
              v-model:value="config[field.name]"
              class="w-full"
              v-bind="getCommonAttrs(field)"
              @blur="handleBlur(field.name)"
              @update:value="(newValue) => updateValue(field.name, newValue)"
            />
          </template>
          <template v-else-if="field.type === FieldTypes.TEXT">
            <CInputPlaceholder
              v-if="showKeepAsIsPlaceholder && !isEditingFieldOrNonEmpty(field.name)"
              @click="enableEditing(field.name, field.type)"
            />
            <NInput
              v-else
              :ref="(el) => (inputRefs[field.name] = el as InstanceType<typeof NInput> | null)"
              v-bind="getCommonAttrs(field)"
              v-model:value="config[field.name]"
              type="text"
              @blur="handleBlur(field.name)"
              @update:value="(newValue) => (config[field.name] = newValue || null)"
            />
          </template>
          <template v-else-if="field.type === FieldTypes.DATE">
            <CInputPlaceholder
              v-if="showKeepAsIsPlaceholder && !isEditingFieldOrNonEmpty(field.name)"
              @click="enableEditing(field.name, field.type)"
            >
              <template #suffix>
                <CIcon fa-type="fal" icon="calendar" size="16px" />
              </template>
            </CInputPlaceholder>
            <NDatePicker
              v-else
              :ref="(el) => (inputRefs[field.name] = el as InstanceType<typeof NDatePicker> | null)"
              v-model:formatted-value="config[field.name]"
              v-bind="getCommonAttrs(field)"
              class="w-full"
              clearable
              :on-clear="() => handleBlur(field.name)"
              :to="false"
              type="date"
              value-format="yyyy-MM-dd"
              @blur="handleBlur(field.name)"
              @update:formatted-value="(newValue) => updateValue(field.name, newValue)"
            />
          </template>
          <template v-else-if="[FieldTypes.SELECT, FieldTypes.MULTI_SELECT].includes(field.type)">
            <CInputPlaceholder
              v-if="showKeepAsIsPlaceholder && !isEditingFieldOrNonEmpty(field.name)"
              @click="enableEditing(field.name, field.type)"
            >
              <template #suffix>
                <CIcon fa-type="fal" icon="angle-down" size="16px" />
              </template>
            </CInputPlaceholder>
            <NSelect
              v-else
              :ref="(el) => (inputRefs[field.name] = el as InstanceType<typeof NSelect> | null)"
              v-model:value="config[field.name]"
              v-bind="getCommonAttrs(field)"
              clearable
              filterable
              :multiple="FieldTypes.MULTI_SELECT === field.type"
              :options="field.options"
              :to="false"
              @blur="handleBlur(field.name)"
              @clear="handleBlur(field.name)"
              @update:value="(newValue) => updateValue(field.name, newValue)"
            />
          </template>
          <template v-else-if="field.type === FieldTypes.CHECKBOX">
            <template v-if="showKeepAsIsPlaceholder">
              <CInputPlaceholder
                v-if="!isEditingFieldOrNonEmpty(field.name)"
                @click="enableEditing(field.name, field.type)"
              >
                <template #suffix>
                  <CIcon fa-type="fal" icon="angle-down" size="16px" />
                </template>
              </CInputPlaceholder>
              <NSelect
                v-else
                :ref="(el) => (inputRefs[field.name] = el as InstanceType<typeof NSelect> | null)"
                v-model:value="config[field.name]"
                v-bind="getCommonAttrs(field)"
                clearable
                filterable
                :options="[
                  { label: t('canvasUI.common.false'), value: 'false' },
                  { label: t('canvasUI.common.true'), value: 'true' },
                ]"
                :to="false"
                @blur="handleBlur(field.name)"
                @clear="handleBlur(field.name)"
                @update:value="(newValue) => updateValue(field.name, newValue)"
              />
            </template>
            <NCheckbox
              v-else
              v-model:checked="config[field.name]"
              v-bind="getCommonAttrs(field)"
              @update:checked="(newValue: any) => updateValue(field.name, newValue)"
            />
          </template>
        </NFormItem>
      </div>
    </NForm>
  </NSpace>
</template>

<script setup lang="ts">
import { nextTick, ref, watchEffect } from 'vue'
import {
  NDatePicker,
  NForm,
  NFormItem,
  NInput,
  NInputNumber,
  NSelect,
  NCheckbox,
  NSpace,
  type FormItemRule,
} from 'naive-ui'
import { isEmpty } from 'lodash-es'
import { useI18n } from 'vue-i18n'
import dayjs from 'dayjs'

import CIcon from '@lib/components/CIcon/CIcon.vue'
import CInputPlaceholder from '@lib/components/CInputPlaceholder/CInputPlaceholder.vue'

import { getValidationRules } from './CConfigEditor.validators'
import type { ConfigFormSection, ConfigForm } from './CConfigEditor.types'
import { type MetadataTemplateField, MetadataTemplateFieldTypeEnum as FieldTypes } from '@openapi'
import useThemeVars from '@lib/composables/useThemeVars'
import { toCssClassKey } from '@lib/util/toCssClassKey'

const themeVars = useThemeVars()
const { t } = useI18n()
const props = withDefaults(
  defineProps<{
    template?: ConfigForm | null
    loading?: boolean
    initialValues?: Record<string, any>
    readOnly?: boolean
    showKeepAsIsPlaceholder?: boolean
    ignoreRequired?: boolean
  }>(),
  {
    showKeepAsIsPlaceholder: false,
    loading: false,
    readOnly: false,
    ignoreRequired: false,
  },
)

const emit = defineEmits<{
  change: [config: Record<string, any>]
}>()

const configFormRef = ref<null | InstanceType<typeof NForm>>()
const config = ref({} as Record<string, any>)
const rules = ref({} as Record<string, FormItemRule>)
const editingField = ref<string | null>(null)
const inputRefs = ref<
  Record<string, InstanceType<typeof NSelect | typeof NInput | typeof NDatePicker | typeof NInputNumber> | null>
>({})

const getCommonAttrs = (field: MetadataTemplateField) => ({
  label: field.label,
  required: field.is_required && !props.ignoreRequired,
  placeholder: field.help_text,
  disabled: props.readOnly || props.loading,
})

watchEffect(() => {
  if (!props.initialValues || !props.template) return

  const sections = props.template.sections ?? []

  sections.forEach((section: ConfigFormSection) => {
    section.fields.forEach((field: MetadataTemplateField) => {
      rules.value[field.name] = getValidationRules(field, t, { ignoreRequired: props.ignoreRequired })
    })
  })

  // Filter out invalid values for SELECT and MULTI_SELECT fields that exist in initialValues
  const filteredInitialValues = !isEmpty(props.initialValues) ? { ...props.initialValues } : {}

  if (!isEmpty(props.initialValues)) {
    sections.forEach((section: ConfigFormSection) => {
      section.fields.forEach((field: MetadataTemplateField) => {
        // Only process fields that exist in initialValues
        if (
          field.name in filteredInitialValues &&
          (field.type === FieldTypes.SELECT || field.type === FieldTypes.MULTI_SELECT)
        ) {
          const validValues = field.options?.map((option) => option.value) ?? []
          const currentValue = filteredInitialValues[field.name]

          if (field.type === FieldTypes.MULTI_SELECT) {
            // For multi-select, ensure it's an array and filter out invalid values
            if (Array.isArray(currentValue)) {
              const filteredValues = currentValue.filter((value) => validValues.includes(value))
              filteredInitialValues[field.name] = filteredValues.length > 0 ? filteredValues : null
            } else {
              // If not an array, set to null
              filteredInitialValues[field.name] = null
            }
          } else if (field.type === FieldTypes.SELECT) {
            // For single select, set to null if value is not valid (including falsy values)
            if (currentValue !== null && currentValue !== undefined && !validValues.includes(currentValue)) {
              filteredInitialValues[field.name] = null
            }
          }
        }
      })
    })
  }

  // Build config: use filteredInitialValues where field exists, apply defaults only for missing fields
  // Explicit null is a valid saved value and should be preserved
  config.value = sections.reduce((acc: Record<string, any>, section: ConfigFormSection) => {
    section.fields.forEach((field: MetadataTemplateField) => {
      const fieldExists = field.name in filteredInitialValues
      if (fieldExists) {
        acc[field.name] = filteredInitialValues[field.name]
      } else if (field.default == null || field.default === '') {
        // No default or empty string - treat as null (empty string breaks date pickers)
        acc[field.name] = null
      } else if (field.type === FieldTypes.MULTI_SELECT && Array.isArray(field.default)) {
        const validOptions = new Set(field.options?.map((opt) => opt.value))
        const validDefaults = field.default.filter((v) => validOptions.has(v))
        acc[field.name] = validDefaults.length > 0 ? validDefaults : null
      } else if (field.type === FieldTypes.SELECT) {
        const validOptions = new Set(field.options?.map((opt) => opt.value))
        acc[field.name] = validOptions.has(field.default as string) ? field.default : null
      } else {
        acc[field.name] = field.default
      }
    })
    return acc
  }, {})
})

function isEditingFieldOrNonEmpty(fieldName: string) {
  return editingField.value === fieldName || !!config.value[fieldName]
}

function enableEditing(fieldName: string, fieldType: FieldTypes) {
  editingField.value = fieldName

  nextTick(() => {
    const input = inputRefs.value[fieldName] as InstanceType<
      typeof NSelect | typeof NInput | typeof NDatePicker | typeof NInputNumber
    > & { $el: HTMLElement }
    if (!input) return
    switch (fieldType) {
      case FieldTypes.TEXT:
      case FieldTypes.NUMBER:
        ;(input as any).focus()
        break
      case FieldTypes.DATE: {
        const selectionInput = input.$el?.querySelector('.n-input__input-el')
        selectionInput?.click()
        break
      }
      case FieldTypes.CHECKBOX:
      case FieldTypes.SELECT: {
        const selectionInput = input.$el?.querySelector('.n-base-selection-input')
        selectionInput?.click()
        ;(input as any)?.focusInput()
        break
      }
      case FieldTypes.MULTI_SELECT: {
        const selectionInput = input.$el?.querySelector('.n-base-selection-input-tag__input')
        selectionInput?.click()
        ;(input as any)?.focusInput()
        break
      }
      default:
        console.warn(`Unhandled field type: ${fieldType}`)
    }
  })
}

async function handleBlur(fieldName: string) {
  await nextTick()
  const fieldValue = config.value[fieldName]
  if (
    fieldValue === null ||
    fieldValue === undefined ||
    fieldValue === '' ||
    (Array.isArray(fieldValue) && fieldValue.length === 0)
  ) {
    editingField.value = null
    config.value[fieldName] = null
  }
  emit('change', config.value)
}

function updateValue(fieldName: string, newValue: any) {
  if (newValue === '' || newValue === null || newValue === undefined) {
    config.value[fieldName] = null
  } else if (newValue instanceof Date) {
    config.value[fieldName] = dayjs(newValue).format('YYYY-MM-DD')
  } else if (Array.isArray(newValue) && newValue.length === 0) {
    config.value[fieldName] = null
  } else {
    config.value[fieldName] = newValue
  }
  emit('change', config.value)
}

async function validate() {
  try {
    await configFormRef.value?.validate()

    return { config: config.value, success: true as const }
  } catch (err) {
    let firstErrorFieldName: string | undefined
    if (Array.isArray(err)) {
      const first = err?.[0]?.[0] as { field?: string; fieldValue?: any; message?: string } | undefined
      firstErrorFieldName = first?.field
    } else {
      console.error(`Unexpected error on validation: `, err)
    }
    return { config: config.value, errors: err, firstErrorFieldName, success: false as const }
  }
}

defineExpose({ validate })
</script>

<style scoped lang="scss">
.template-section--has-next {
  border-bottom: 1px solid v-bind('themeVars.primary5');
}
</style>
